import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [["proximo", "Próximos"], ["vencido", "Vencidos"], ["futuro", "Futuros"], ["programado", "OS creada"], ["suspendido", "Suspendidos"], ["atendido", "Atendidos"], ["cancelado", "Cancelados"]];
const TONE = { vencido: "text-red-400", proximo: "text-amber-300", programado: "text-sky-300", suspendido: "text-orange-400", atendido: "text-emerald-400" };

const when = (o) => (o.bucket === "vencido" ? `Vencido hace ${-o.days_left} días` : o.days_left === 0 ? "Hoy" : o.days_left > 0 ? `En ${o.days_left} días` : `Hace ${-o.days_left} días`);

const Row = ({ o, canCreate, onCreate, busy }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border bg-card/40 px-4 py-3" data-testid={`maint-row-${o.source_wo_folio}`}>
    <div className="min-w-[180px] flex-1">
      <div className="font-medium">{o.client_name}{o.project_name && <span className="text-muted-foreground"> / {o.project_name}</span>}</div>
      <div className="text-xs text-muted-foreground">{o.business_unit_name} · OS origen <Link className="text-primary" to={`/admin/os/${o.source_wo_id}`} data-testid={`maint-source-link-${o.source_wo_folio}`}>{o.source_wo_folio}</Link>{o.periodicity_months ? ` · cada ${o.periodicity_months} meses` : ""}{o.migrated ? " · migrado" : ""}</div>
    </div>
    <div className="text-sm"><div data-testid={`maint-due-${o.source_wo_folio}`}>{fmtDate(o.due_date)}</div><div className={cn("text-xs", TONE[o.bucket])}>{o.status === "pendiente" ? when(o) : o.status_label}</div></div>
    {o.maintenance_wo_id && <Link className="font-mono text-sm text-primary" to={`/admin/os/${o.maintenance_wo_id}`} data-testid={`maint-wo-link-${o.source_wo_folio}`}>{o.maintenance_wo_folio}</Link>}
    {canCreate && o.status === "pendiente" && <Button size="sm" onClick={() => onCreate(o)} disabled={busy} data-testid={`maint-create-wo-${o.source_wo_folio}`}><Wrench className="mr-1 h-4 w-4" />Crear OS de mantenimiento</Button>}
  </div>
);

export default function Maintenance() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("proximo");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/maintenance").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (o) => {
    if (!window.confirm(`¿Crear la OS de mantenimiento para ${o.client_name} (OS origen ${o.source_wo_folio})?`)) return;
    setBusy(true);
    try { const { data: r } = await api.post(`/maintenance/${o.id}/create-wo`, {}); toast.success(`OS ${r.folio} creada`); nav(`/admin/os/${r.work_order_id}`); }
    catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(false); }
  };
  const rows = (data?.rows || []).filter((o) => o.bucket === tab);
  return (
    <div data-testid="maintenance-page">
      <PageHeader eyebrow="Fase 4" title="Mantenimientos" />
      <p className="mb-4 text-sm text-muted-foreground">Próximos: fecha dentro de los siguientes {data?.window_days ?? 30} días. Crear la OS es manual; no se generan OS automáticamente.</p>
      <div className="mb-5 flex flex-wrap gap-2" data-testid="maint-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`maint-tab-${k}`}
            className={cn("rounded-lg border px-3 py-2 text-sm transition-colors", tab === k ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40")}>
            {l} <span className="ml-1 font-mono" data-testid={`maint-count-${k}`}>{data?.counts?.[k] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="maint-list">
        {rows.map((o) => <Row key={o.id} o={o} canCreate={can("wo.write")} onCreate={create} busy={busy} />)}
        {data && !rows.length && <p className="text-sm text-muted-foreground" data-testid="maint-empty">Sin mantenimientos en esta vista.</p>}
      </div>
    </div>
  );
}
